// This class contains the necessary method of gathering item data for the good and services form
// getFormMappings() would return an array of FoxIt fields with the corresponding value
import { FormMapping } from '@/components/documents/Foxit/FormMapping/FormMapping'
import {
	type checkBoxMapConfig,
	gMerchandiseMapConfig,
	gServiceCheckBoxMapConfig,
	gServiceMapSubItemsConfig,
	ngCashAdvanceMapConfig,
	ngCemeteryCheckBoxMapConfig,
	PriceTotalKey,
	priceTotalMapConfig,
	type sectionMapConfig,
} from '@/components/documents/Foxit/FormMapping/GoodsAndServicesMapConfig'
import {
	Pb,
	type MappedCustomItem,
	type ServiceLineItem,
	type MerchandiseLineItem,
	type CashAdvanceLineItem,
} from '@/components/documents/Foxit/FormMapping/formMappingTypes'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'
import {
	type CartItemAndLineItem,
	type CartSubItemAndLineItem,
} from '@/mixins/merchandise'
import type { CustomCartItem } from '@/types'
import type { ScenarioOutput } from '@precoa/calculator-sdk'
import { captureException } from '@sentry/vue'

export default class GoodsAndServicesForm extends FormMapping {
	protected readonly formatPrice: (
		price: number | null | undefined
	) => '' | string
	protected readonly translate: ((key: string) => string) | undefined
	private readonly cartItems: CartItemAndLineItem[] | undefined
	protected servicesMap = new Map<
		PbProductType | PbSubcategory | string,
		any[]
	>()
	protected merchandiseMap = new Map<
		PbProductType | PbSubcategory | string,
		any[]
	>()
	protected cashAdvanceMap = new Map<
		PbProductType | PbSubcategory | string,
		any[]
	>()
	protected packageIsBundle: boolean
	private softDeletedServiceSubItemPrice: number
	private packageIncludedMerchandisePrice: number
	private packageIncludedCashAdvancePrice: number
	protected fieldPrices: Record<string, number> = {}
	protected activePriceBookVersion: number | undefined
	// Items that share a line are listed on the supplemental page
	protected readonly hasSupplementalPage: boolean = true
	// Refrigeration is priced on the embalming line
	protected readonly sharesEmbalmingAndRefrigerationLine: boolean = true

	private gServicesSupField = new Map<PbSubcategory | string, string>()
	private gMerchandiseSupField = new Map<PbSubcategory | string, string>()
	protected ngCashAdvanceSupField = new Map<PbSubcategory | string, string>()

	// Seems redundant but few subcategories are repeated in sections.
	// Having separate sets makes sure supplemental page description has unique lines for each item
	private addedServiceSupplementalCategories = new Set<string>()
	private addedMerchandiseSupplementalCategories = new Set<string>()
	private addedCashAdvanceSupplementalCategories = new Set<string>()

	private readonly serviceCategories = [
		PbProductType.Disposition,
		PbProductType.Package,
		PbProductType.Service,
		PbProductType.Transportation,
	]

	private readonly cashAdvanceCategories = [
		PbProductType.CashAdvance,
		PbProductType.FamilyTravel,
		PbProductType.Cemetery,
		PbProductType.Afterward,
		PbProductType.Other,
	]

	private readonly cemeteryTypes = [
		Pb.CemeteryPlot,
		Pb.CemeteryMausoleum,
		Pb.CemeteryColumbarium,
	]

	constructor(
		planHolderId: string,
		cartItems?: CartItemAndLineItem[],
		formatPrice?: (price: number | null | undefined) => '' | string,
		translationFunction?: ((key: string) => string) | undefined,
		activePriceBookVersion?: number,
		paymentDetails?: ScenarioOutput
	) {
		super(planHolderId)
		this.formatPrice = formatPrice!
		this.translate = translationFunction
		this.cartItems = cartItems
		this.packageIsBundle = true
		this.softDeletedServiceSubItemPrice = 0
		this.packageIncludedMerchandisePrice = 0
		this.packageIncludedCashAdvancePrice = 0
		this.activePriceBookVersion = activePriceBookVersion
	}

	// V2 Pricebooks only mark as 'Incl' if the price on the subitem is 0
	protected getItemPriceByPriceBookVersion(
		cartSubItem: CartItemAndLineItem | CartSubItemAndLineItem,
		line: ServiceLineItem | MerchandiseLineItem,
		isPackageIncludedItem: boolean,
		section: 'service' | 'merchandise' | 'cashAdvance'
	) {
		if (this.activePriceBookVersion === 2) {
			return cartSubItem.price === 0
				? 'Incl'
				: (cartSubItem.price ?? line.fixedPrice ?? '') *
						(cartSubItem.quantity ?? 1)
		}

		if (section === 'service') {
			return isPackageIncludedItem || cartSubItem.price === 0
				? 'Incl'
				: (cartSubItem.price ?? line.fixedPrice ?? '') *
						(cartSubItem.quantity ?? 1)
		}

		if (section === 'merchandise') {
			return isPackageIncludedItem && cartSubItem.price === 0
				? 'Incl'
				: (cartSubItem.price ?? line.fixedPrice ?? '') *
						(cartSubItem.quantity ?? 1)
		}

		if (section === 'cashAdvance') {
			return (
				(cartSubItem.price ?? line.fixedPrice ?? '') *
				(cartSubItem.quantity ?? 1)
			)
		}

		return 0
	}

	// Helper to set cartItems that are subItems in a package/bundle
	protected setMappedServiceSubItem = (
		key: string,
		isIncludedInPkg: boolean,
		cartSubItem?: CartItemAndLineItem | CartSubItemAndLineItem,
		customItem?: MappedCustomItem
	) => {
		let line
		let item
		if (cartSubItem) {
			line = cartSubItem.lineItem as ServiceLineItem
			if (!line) return

			const isPackageIncludedItem = !this.packageIsBundle && isIncludedInPkg
			const itemPrice = this.getItemPriceByPriceBookVersion(
				cartSubItem,
				line,
				isPackageIncludedItem,
				'service'
			)

			item = {
				name: line.name,
				isBundle: line.isBundle ?? false,
				category: line.category ?? '',
				subcategory: line.subcategory ?? '',
				isGuaranteed: line.isGuaranteed,
				price: itemPrice,
				mileRadiusForStandardService: line.mileRadiusForStandardService ?? '',
				amountPerMile: line.amountPerMile ?? '',
				quantity: cartSubItem.quantity ?? 1,
				numberOfDays: line.numberOfDays ?? '',
				shortDescription:
					line.shortDescription ??
					(line.subcategory === Pb.Discount ? line.name : ''),
			}

			if (
				item.category === PbProductType.Transportation &&
				item.subcategory === Pb.Discount
			) {
				key = `${PbProductType.Transportation}-${Pb.Other}`
			}

			if (item.category === PbProductType.Package) {
				this.servicesMap.set(`${PbProductType.Transportation}-${Pb.Other2}`, [
					item,
				])
			}
		}

		if (customItem) {
			item = customItem
		}

		// These items share a line on the form, so we need to combine them into one key/subcategory
		if (
			item?.subcategory === Pb.ServiceReceivingRemains ||
			item?.subcategory === Pb.ServiceForwardingRemains
		) {
			item.subcategory = Pb.ServiceForwardingAndReceivingRemains
			key = Pb.ServiceForwardingAndReceivingRemains
		}

		if (key.includes(Pb.Other)) {
			key = this.getOtherLineKey(item)
		}

		let servicesSubCatArray = this.servicesMap.get(key)
		if (!servicesSubCatArray) {
			this.servicesMap.set(key, [])
			servicesSubCatArray = []
		}

		this.servicesMap.set(key, [...servicesSubCatArray, item])
	}

	// Helper to set cartItems that are guaranteed merchandise
	protected setMappedMerchandise(
		key: string,
		isIncludedInPkg: boolean,
		cartItem?: CartItemAndLineItem | CartSubItemAndLineItem,
		customItem?: MappedCustomItem
	) {
		let line
		let item

		if (cartItem) {
			line = cartItem.lineItem as MerchandiseLineItem
			if (!line) return

			const isPackageIncludedItem = !this.packageIsBundle && isIncludedInPkg
			const itemPrice = this.getItemPriceByPriceBookVersion(
				cartItem,
				line,
				isPackageIncludedItem,
				'merchandise'
			)

			item = {
				name: line?.name,
				category: line?.category ?? '',
				subcategory: line?.subcategory ?? '',
				interiorDescription: line?.interiorDescription ?? '',
				exteriorDescription: line?.exteriorDescription ?? '',
				modelNameNumber: line?.modelNameNumber ?? '',
				productManufacturer: line?.productManufacturer ?? '',
				isGuaranteed: line?.isGuaranteed,
				price: itemPrice,
				quantity: cartItem.quantity ?? 1,
				shortDescription:
					(line?.shortDescription ?? line?.subcategory === Pb.Discount)
						? line.name
						: '',
			}
		}

		if (customItem) {
			item = customItem
		}

		if (
			item?.category === PbProductType.Merchandise &&
			item?.subcategory === Pb.Discount
		) {
			key = `${PbProductType.Merchandise}-${Pb.Other}`
		}

		if (key.includes(Pb.Other)) {
			key = this.getOtherLineKey(item)
		}

		let merchandiseSubCatArray = this.merchandiseMap.get(key)
		if (!merchandiseSubCatArray) {
			this.merchandiseMap.set(key, [])
			merchandiseSubCatArray = []
		}

		this.merchandiseMap.set(key, [...merchandiseSubCatArray, item])
	}

	// Helper to set cartItems that can be a package/bundle or cashAdvanced items
	private setMappedCashAdvanceItem = (
		key: PbProductType | PbSubcategory | string,
		cartItem?: CartItemAndLineItem | CartSubItemAndLineItem,
		customItem?: MappedCustomItem
	) => {
		let line
		let item
		if (cartItem) {
			line = cartItem.lineItem as CashAdvanceLineItem

			if (!line) {
				return
			}

			const itemPrice = this.getItemPriceByPriceBookVersion(
				cartItem,
				line,
				false,
				'cashAdvance'
			)

			item = {
				name: line.name,
				amountPerDeathCertificate:
					line.amountPerDeathCertificate ?? line.fixedPrice ?? '',
				category: line.category ?? '',
				subcategory: line.subcategory ?? '',
				isGuaranteed: line.isGuaranteed,
				price: itemPrice,
				quantity: cartItem.quantity ?? 1,
				shortDescription: line.shortDescription ?? '',
			}
		}

		if (customItem) {
			item = customItem
		}

		if (
			item?.category === PbProductType.Other &&
			item?.subcategory === Pb.Discount
		) {
			key = `${PbProductType.Other}-${Pb.Other}`
		}

		if (key.includes(Pb.Other)) {
			key = this.getOtherLineKey(item)
		}

		// if there is no key for a certain subcategory, set the value to an empty array
		let cashAdvanceSubCatArray = this.cashAdvanceMap.get(key)
		if (!cashAdvanceSubCatArray) {
			this.cashAdvanceMap.set(key, [])
			cashAdvanceSubCatArray = []
		}

		this.cashAdvanceMap.set(key, [...cashAdvanceSubCatArray, item])
	}

	// Helper to determine which itemMap does a customItem goes into
	private getCustomItemSectionKey = (
		category: PbProductType
	): PbProductType => {
		if (this.serviceCategories.includes(category)) {
			return PbProductType.Service
		}

		if (this.cashAdvanceCategories.includes(category)) {
			return PbProductType.CashAdvance
		}

		return PbProductType.Merchandise
	}

	// Helper to set customCartItems
	private setMappedCustomItem = (
		customItem: CustomCartItem,
		key: string,
		category: PbProductType
	) => {
		// Custom Cart Item prices are set by the AFP and should not be multiplied by the quantity.
		const item: MappedCustomItem = {
			...customItem,
			name: customItem.title ?? '',
			price: Number(customItem.price ?? 0),
			quantity: customItem.quantity ?? 1,
		}

		const section = this.getCustomItemSectionKey(category)

		switch (section) {
			case PbProductType.Service:
				item.price = item.price === 0 ? 'Incl' : item.price
				this.setMappedServiceSubItem(key, false, undefined, item)
				break
			case PbProductType.Merchandise:
				item.price = item.price === 0 ? 'Incl' : item.price
				this.setMappedMerchandise(key, false, undefined, item)
				break
			case PbProductType.CashAdvance:
				this.setMappedCashAdvanceItem(key, undefined, item)
				break
		}
	}

	// This method filters the incoming cartItems to the appropriate section map which is keyed with a subcategory or custom key that matches a G&S form field
	protected extractCleanedLineItems(): void {
		if (!this.cartItems) return

		this.cartItems.forEach((cartItem) => {
			// ignore softDeleted items, it means they are deleted from the cart (Review page)
			if (cartItem.isSoftDeleted) return
			// For Section A package/bundle (Guaranteed)
			if (cartItem.lineItem?.category === PbProductType.Package) {
				const packageLineItem = cartItem.lineItem as ServiceLineItem
				// If it's a package, we want the package name on Other Line 2, map it into Other2 for Service
				if (!packageLineItem.isBundle) {
					this.packageIsBundle = false
					this.setMappedServiceSubItem(
						PbProductType.Package,
						false,
						cartItem,
						undefined
					)
				}

				if (cartItem.subItems.length === 0) {
					return
				}

				cartItem.subItems.forEach((subItem: CartSubItemAndLineItem) => {
					if (subItem.lineItem?.isVirtualLine) {
						return
					}

					if (subItem.isSoftDeleted) {
						this.softDeletedServiceSubItemPrice +=
							subItem.price ?? subItem.lineItem?.fixedPrice ?? 0
						return
					}

					// Package may contain items from any category, determine which section they fall into and set it accordingly
					const section = this.getCustomItemSectionKey(
						subItem.lineItem?.category!
					)

					const key = this.cemeteryTypes.includes(
						subItem.lineItem?.subcategory!
					)
						? subItem.lineItem?.category!
						: subItem.lineItem?.subcategory!

					switch (section) {
						case PbProductType.Service:
							this.setMappedServiceSubItem(key, true, subItem, undefined)
							break
						case PbProductType.Merchandise:
							this.setMappedMerchandise(key, true, subItem, undefined)
							this.packageIncludedMerchandisePrice +=
								subItem.price ?? subItem.lineItem?.fixedPrice ?? 0
							break
						case PbProductType.CashAdvance:
							this.setMappedCashAdvanceItem(key, subItem, undefined)
							if (
								packageLineItem.subcategory !== PbSubcategory.PackageEssentials
							) {
								this.packageIncludedCashAdvancePrice +=
									subItem.price ?? subItem.lineItem?.fixedPrice ?? 0
							}
							break
					}
				})
				return
			}

			// For Section B merchandise (Guaranteed)
			if (cartItem.lineItem?.category === PbProductType.Merchandise) {
				this.setMappedMerchandise(
					cartItem.lineItem?.subcategory!,
					false,
					cartItem,
					undefined
				)
				return
			}

			// If it's Cemetery, we only want one checkbox and price, therefore it needs to be keyed with 'Cemetery'
			if (this.cemeteryTypes.includes(cartItem.lineItem?.subcategory!)) {
				this.setMappedCashAdvanceItem(
					cartItem.lineItem?.category!,
					cartItem,
					undefined
				)
				return
			}

			// else it's added from the review page, with merchandise accounted for, this can be a cashItem or a serviceItem that's not included in the package
			const section = this.getCustomItemSectionKey(cartItem.lineItem?.category!)
			switch (section) {
				case PbProductType.Service:
					// additional service sub items here are not included in the package
					this.setMappedServiceSubItem(
						cartItem.lineItem?.subcategory!,
						false,
						cartItem,
						undefined
					)
					break

				case PbProductType.CashAdvance:
					this.setMappedCashAdvanceItem(
						cartItem.lineItem?.subcategory!,
						cartItem,
						undefined
					)
					break
			}
		})

		// map custom items, anything that comes from the manual modal on 'Review' section in the presentation
		// cremation is extracted here as there's no non-custom item for CremationServices
		const customCartItems = this.planHolder.customCartItems

		customCartItems.forEach((customItem) => {
			if (customItem.isSoftDeleted) return

			const key =
				this.getOtherLineKey(customItem) ||
				this.getCremationKey(customItem) ||
				(customItem.subcategory as string)

			if (!key) {
				captureException(new Error('No key found for custom item'), {
					extra: {
						customItem,
					},
				})
				return
			}

			this.setMappedCustomItem(customItem, key, customItem.category)
		})
	}

	// This helper returns the sum of package-included merchandise and ng items. Used to subtract them from the total package price
	protected getNonSectionAPackageItemSum(): number {
		return (
			this.softDeletedServiceSubItemPrice +
			this.packageIncludedMerchandisePrice +
			this.packageIncludedCashAdvancePrice
		)
	}

	// Helper to return the appropriate section key for other line items (Package is mapped into Service-Other2)
	protected getOtherLineKey(item: any): string {
		const key = item.subcategory

		if (item.category === PbProductType.Package) {
			return `${PbProductType.Transportation}-${Pb.Other2}`
		}

		if (key === Pb.Other || key === Pb.Other2) {
			return `${item.category}-${item.subcategory}`
		}
		// Since Discount and Other share one line, let them go into the same array
		if (key === Pb.Discount && item.category === PbProductType.Service) {
			return `${PbProductType.Transportation}-${Pb.Other}`
		}

		if (key === Pb.Discount) {
			return `${item.category}-${Pb.Other}`
		}

		return ''
	}

	// Helper to determine whether cremation service is guaranteed, if so give it a custom key
	private getCremationKey = (customItem: CustomCartItem) => {
		const cremationName = Pb.ServiceCremationService
		if (customItem.subcategory !== cremationName) return ''
		if (customItem.category === PbProductType.Service) {
			return `${cremationName}`
		}
		return cremationName
	}

	protected setItemValueForSectionItems(
		category: PbProductType | PbSubcategory | string,
		sectionItemsMap: Map<string, any[]>,
		prop: string,
		sharedRefrigeration: boolean = true
	) {
		let mapItem = sectionItemsMap.get(category)
		let itemValue: string | number = ''
		let refrigerationItem: any[] | undefined

		// If refrigeration item found but no embalming item found, use refrigeration item price instead
		if (
			sharedRefrigeration &&
			category === Pb.ServiceEmbalming &&
			prop === 'price'
		) {
			refrigerationItem = sectionItemsMap.get(Pb.ServiceRefrigeration)

			if (refrigerationItem && !mapItem) {
				itemValue =
					refrigerationItem[0].price !== 'Incl'
						? parseFloat(refrigerationItem[0].price)
						: 'Incl'
			}
		}

		if (mapItem && mapItem.length === 1) {
			const fieldPrice = this.fieldPrices[category]

			itemValue =
				prop === 'price' && mapItem[0]?.[prop] !== 'Incl' && fieldPrice
					? fieldPrice
					: mapItem[0]?.[prop]

			// Add refrigeration price to embalming if refrigeration exists and isn't included
			if (
				refrigerationItem &&
				refrigerationItem[0].price !== 'Incl' &&
				itemValue
			) {
				const refrigerationPrice = parseFloat(refrigerationItem[0].price)
				const currentPrice =
					itemValue === 'Incl' ? 0 : parseFloat(itemValue.toString())
				itemValue = currentPrice + refrigerationPrice
			}
		}

		// Every price branch above yields a raw number or 'Incl'; this is the only
		// place a price gets formatted
		if (prop === 'price' && typeof itemValue === 'number') {
			return itemValue.toFixed(2)
		}

		return itemValue ?? ''
	}

	// this helper takes a section Map Config, goes through each field and returns a form mapping array
	// if there's only 1 item, it returns its description or price
	// if there's more than 1 item, it sets fields that aren't 'price' to 'See Supplemental Page', and set the price to sum of all items in that subcategory
	// ** Price won't be calculated if it's a package-included item
	// Special price calculation for embalming and refrigeration, since they use the same price line
	protected generateFieldMappingsFromSectionConfig(
		sectionConfig: sectionMapConfig,
		sectionItemsMap: Map<string, any[]>,
		existingSubcategoriesInSupPage: Set<string>,
		supplementalFieldMap: Map<PbSubcategory | string, string>
	) {
		return sectionConfig.map(({ name, category, prop }) => {
			let mapItem = sectionItemsMap.get(category)
			let itemValue: string | number = ''

			itemValue = this.setItemValueForSectionItems(
				category,
				sectionItemsMap,
				prop
			)

			if (mapItem && mapItem.length > 1) {
				// check whether items has been listed in supplemental page, if not, do so and mark the category as added
				if (!existingSubcategoriesInSupPage.has(category)) {
					mapItem.forEach((item) => {
						const itemShortDescription = item.shortDescription
							? ` (${item.shortDescription})`
							: ''
						const itemPrice =
							item.category === PbProductType.Package
								? item.price - this.getNonSectionAPackageItemSum()
								: item.price
						const itemPriceText =
							item.price === 'Incl' ? 'Incl' : `${this.formatPrice(itemPrice)}`
						const itemDescription = ` \t${item.name}:${itemShortDescription} ${itemPriceText}\n`

						const key = category.includes(Pb.Other)
							? this.getOtherLineKey(item)
							: (item.subcategory as string)
						const currentDescription = supplementalFieldMap.get(key)
						if (!currentDescription) {
							supplementalFieldMap.set(key, itemDescription)
						} else {
							supplementalFieldMap.set(
								key,
								currentDescription + itemDescription
							)
						}
					})
					existingSubcategoriesInSupPage.add(category)
				}
				itemValue =
					prop === 'price'
						? this.fieldPrices[category] === 0
							? 'Incl'
							: (this.fieldPrices[category].toFixed(2) ?? '')
						: 'See Supplemental Page'
			}

			return {
				name,
				value: itemValue ?? '',
			}
		})
	}

	protected getServicesMapConfig = () => {
		return gServiceMapSubItemsConfig
	}
	protected getMerchandiseMapConfig = () => {
		return gMerchandiseMapConfig
	}
	protected getCashAdvanceMapConfig = () => {
		return ngCashAdvanceMapConfig
	}
	protected getServicesCheckBoxMapConfig = (): checkBoxMapConfig => {
		return gServiceCheckBoxMapConfig
	}
	protected getCashAdvanceCheckBoxMapConfig = (): checkBoxMapConfig => {
		return ngCemeteryCheckBoxMapConfig
	}

	// Checks each checkbox whose category has an item in the section
	protected generateCheckBoxMappingsFromConfig(
		config: checkBoxMapConfig,
		sectionItemsMap: Map<string, any[]>
	) {
		return config.map(({ name, category }) =>
			this.getCheckBoxMapping(name, !!sectionItemsMap.get(category))
		)
	}

	// Returns form filling array for Section A (Guaranteed Services) non-custom line items
	protected getServiceSubItemMappings() {
		const config = this.getServicesMapConfig()
		const servicesFields = this.generateFieldMappingsFromSectionConfig(
			config,
			this.servicesMap,
			this.addedServiceSupplementalCategories,
			this.gServicesSupField
		)

		return [
			...servicesFields,
			...this.generateCheckBoxMappingsFromConfig(
				this.getServicesCheckBoxMapConfig(),
				this.servicesMap
			),
		]
	}

	// Returns form filling array for Section B (Guaranteed Merchandise) non-custom line items
	protected getMerchandiseMappings() {
		const config = this.getMerchandiseMapConfig()
		const merchandiseFields = this.generateFieldMappingsFromSectionConfig(
			config,
			this.merchandiseMap,
			this.addedMerchandiseSupplementalCategories,
			this.gMerchandiseSupField
		)

		return [...merchandiseFields]
	}

	// Helper to determine which cemetery type should be mapped
	private cemeteryTypeIs = (cemeteryType: string): boolean => {
		const existingCemeteryType =
			this.cashAdvanceMap.get(PbProductType.Cemetery) ??
			this.cashAdvanceMap.get(cemeteryType)
		if (!existingCemeteryType) return false
		const selectedCemetery = existingCemeteryType[0]
		return selectedCemetery.subcategory === cemeteryType
	}

	// Returns form filling array for Section C (Non-Guaranteed CashAdvance) non-custom line items
	protected getCashAdvancedMappings() {
		const config = this.getCashAdvanceMapConfig()
		const cashAdvanceFields = this.generateFieldMappingsFromSectionConfig(
			config,
			this.cashAdvanceMap,
			this.addedCashAdvanceSupplementalCategories,
			this.ngCashAdvanceSupField
		)

		return [
			...cashAdvanceFields,
			...this.getCashAdvanceCheckBoxMapConfig().map(({ name, category }) =>
				this.getCheckBoxMapping(name, this.cemeteryTypeIs(category))
			),
		]
	}

	protected getFieldPricesBreakdown(includeDiscounts: boolean = true) {
		this.fieldPrices = {}

		this.tabulateSectionPriceBreakdown(
			this.servicesMap,
			PriceTotalKey.ServicesTotal,
			includeDiscounts
		)
		this.tabulateSectionPriceBreakdown(
			this.merchandiseMap,
			PriceTotalKey.MerchandiseTotal,
			includeDiscounts
		)
		this.tabulateSectionPriceBreakdown(
			this.cashAdvanceMap,
			PriceTotalKey.CashAdvanceTotal,
			includeDiscounts
		)

		this.calculateSectionTotals()
	}

	protected calculateSectionTotals() {
		this.fieldPrices[PriceTotalKey.GoodsAndServicesTotal] =
			(this.fieldPrices[PriceTotalKey.ServicesTotal] ?? 0) +
			(this.fieldPrices[PriceTotalKey.MerchandiseTotal] ?? 0)

		this.fieldPrices[PriceTotalKey.FuneralTotal] =
			this.fieldPrices[PriceTotalKey.GoodsAndServicesTotal] +
			(this.fieldPrices[PriceTotalKey.CashAdvanceTotal] ?? 0)
	}

	private tabulateSectionPriceBreakdown(
		itemMap: Map<string, any[]>,
		sectionTotalFieldName: string,
		includeDiscounts: boolean = true
	) {
		let sectionTotal = 0

		for (const [key, itemArray] of itemMap.entries()) {
			let subcategorySum = 0

			for (const item of itemArray) {
				const price =
					typeof item.price === 'number'
						? item.price
						: parseFloat(item.price) || 0

				subcategorySum += price
			}

			// When the item is a package with the old pb version, subtract the cost of the items from section b and c
			if (
				this.activePriceBookVersion != 2 &&
				!this.packageIsBundle &&
				key === PbProductType.Package
			) {
				subcategorySum -= this.getNonSectionAPackageItemSum()
			}

			this.fieldPrices[key] = subcategorySum

			// If the item is a Transportation Other 2 custom item and the package is not a bundle, don't add it to the section total
			if (
				sectionTotalFieldName === PriceTotalKey.ServicesTotal &&
				key === `${PbProductType.Transportation}-${Pb.Other2}` &&
				!this.packageIsBundle
			) {
				continue
			}

			if (subcategorySum >= 0 || (subcategorySum < 0 && includeDiscounts)) {
				sectionTotal += subcategorySum
			}
		}

		this.fieldPrices[sectionTotalFieldName] = sectionTotal
	}

	// Helper to return the form mapping array for the price total fields
	protected getPriceTotalFieldsMappings() {
		const priceTotalFields = [
			...priceTotalMapConfig.map(({ name, key }) => ({
				name,
				value: this.fieldPrices[key].toFixed(2) ?? '',
			})),
		]

		return priceTotalFields
	}

	// This helper creates the text within each section's supplemental form field
	// It starts a section with the category/productType, and has its associated item lines beneath it
	protected createSupplementalFieldDescription = (
		supFieldMap: Map<PbSubcategory | string, string>
	) => {
		let fieldDescription = ''
		supFieldMap.forEach((description, subcategory) => {
			const localizedSubcategory = this.translate
				? this.getLocalizedSubcategoryTitle(subcategory)
				: subcategory
			const formattedSubtotal = this.formatPrice
				? this.formatPrice(this.fieldPrices[subcategory])
				: `$${this.fieldPrices[subcategory]}`
			fieldDescription += ` ${localizedSubcategory}:\n${description} \tSubtotal: ${formattedSubtotal}\n\n`
		})
		return fieldDescription
	}

	// Helper to get a localized title for sections that goes into Supplemental page. If the category is 'Service-Other', simply return translated 'Other'
	protected getLocalizedSubcategoryTitle = (subcategory: string): string => {
		if (!this.translate) return ''
		const translate = this.translate
		const categories = subcategory.split('-')

		if (categories.length > 1) {
			return translate(`productCategories.${categories[1]}`)
		}
		return translate(`productSubcategories.${subcategory}`)
	}

	protected getSupplementalMappings = () => {
		return [
			{
				name: 'gServicesSupDesc',
				value: this.createSupplementalFieldDescription(this.gServicesSupField),
			},
			{
				name: 'gMerchandiseSupDesc',
				value: this.createSupplementalFieldDescription(
					this.gMerchandiseSupField
				),
			},
			{
				name: 'ngCashAdvanceSupDesc',
				value: this.createSupplementalFieldDescription(
					this.ngCashAdvanceSupField
				),
			},
		]
	}

	getFormMappings() {
		this.extractCleanedLineItems()
		this.getFieldPricesBreakdown()

		return [
			...this.getProposedInsuredMapping(),
			...this.getProposedInsuredAddressMapping(),
			...this.getFuneralHomeRepresentativeMappings(),
			...this.getServiceSubItemMappings(),
			...this.getMerchandiseMappings(),
			...this.getCashAdvancedMappings(),
			...this.getPolicyOwnerMapping(),
			...this.getPolicyOwnerAddressMapping(),
			...this.getFuneralHomeMapping(),
			...this.getSupplementalMappings(),
			...this.getPriceTotalFieldsMappings(),
		]
	}

	// Mappings for only the fields a custom item is filled into, so adding one
	// doesn't overwrite what the AFP entered by hand. Totals are left to the PDF's calculations.
	getCustomItemFormMappings(customItemId: CustomCartItem['id']) {
		const formMappings = this.getFormMappings()
		const fieldNames = this.getCustomItemFieldNames(customItemId)

		return formMappings.filter(({ name }) => fieldNames.includes(name))
	}

	// The custom item's fields are the ones the state's configs list for the line it was mapped to
	protected getCustomItemFieldNames(customItemId: CustomCartItem['id']) {
		const sections = [
			{
				itemsMap: this.servicesMap,
				configs: [
					...this.getServicesMapConfig(),
					...this.getServicesCheckBoxMapConfig(),
				],
				supplementalFieldName: 'gServicesSupDesc',
			},
			{
				itemsMap: this.merchandiseMap,
				configs: this.getMerchandiseMapConfig(),
				supplementalFieldName: 'gMerchandiseSupDesc',
			},
			{
				itemsMap: this.cashAdvanceMap,
				configs: [
					...this.getCashAdvanceMapConfig(),
					...this.getCashAdvanceCheckBoxMapConfig(),
				],
				supplementalFieldName: 'ngCashAdvanceSupDesc',
			},
		]

		for (const { itemsMap, configs, supplementalFieldName } of sections) {
			const [key, items] =
				[...itemsMap.entries()].find(([, items]) =>
					items.some((item) => item.id === customItemId)
				) ?? []

			if (!key || !items) {
				continue
			}

			const fieldNames = [
				...configs
					.filter(({ category }) => category === key)
					.map(({ name }) => name),
				...this.getCustomItemSharedLineFieldNames(key),
			]

			if (this.hasSupplementalPage && items.length > 1) {
				fieldNames.push(supplementalFieldName)
			}

			return fieldNames
		}

		return []
	}

	// Fields outside the configs that a custom item on this line also fills
	protected getCustomItemSharedLineFieldNames(key: string): string[] {
		if (
			!this.sharesEmbalmingAndRefrigerationLine ||
			key !== Pb.ServiceRefrigeration
		) {
			return []
		}

		return this.getServicesMapConfig()
			.filter(
				({ category, prop }) =>
					category === Pb.ServiceEmbalming && prop === 'price'
			)
			.map(({ name }) => name)
	}
}
